import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Gamepad2,
  Music2,
  Play,
  Twitch,
  X,
  Youtube,
} from "lucide-react";

const MINT = "#10b981";
const PURPLE = "#a855f7";

const platformIcon = {
  Twitch,
  YouTube: Youtube,
  TikTok: Music2,
  Gaming: Gamepad2,
};

function CreatorProofBadge({ label, platform, accent = "mint" }) {
  const color = accent === "purple" ? PURPLE : MINT;
  const Icon = platformIcon[platform] ?? Play;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold backdrop-blur-md"
      style={{
        backgroundColor: `${color}22`,
        color,
        border: `1px solid ${color}55`,
      }}
    >
      <Icon size={12} />
      {label}
    </span>
  );
}

function CreatorEditCard({ edit, onOpen }) {
  const accentColor = edit.accent === "purple" ? PURPLE : MINT;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(edit)}
      className="group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] text-left backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:border-white/20"
      style={{ boxShadow: `0 0 0 0 ${accentColor}00` }}
      onMouseEnter={(event) => {
        event.currentTarget.style.boxShadow = `0 20px 60px -20px ${accentColor}55`;
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.boxShadow = `0 0 0 0 ${accentColor}00`;
      }}
    >
      <div className="relative aspect-video overflow-hidden bg-[#0a0a0f]">
        {edit.src ? (
          <video
            src={edit.src}
            poster={edit.thumbnail}
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
            onMouseEnter={(event) => event.currentTarget.play().catch(() => {})}
            onMouseLeave={(event) => {
              event.currentTarget.pause();
              event.currentTarget.currentTime = 0;
            }}
          />
        ) : edit.thumbnail ? (
          <img
            src={edit.thumbnail}
            alt={edit.title}
            className="h-full w-full object-cover transition-all duration-500 group-hover:scale-105 group-hover:brightness-110"
          />
        ) : (
          <div
            className="grid h-full w-full place-items-center"
            style={{
              background: `linear-gradient(135deg, ${accentColor}33 0%, #0a0a0f 70%)`,
            }}
          >
            <Play size={28} className="text-white/30" />
          </div>
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-[#07060d] via-[#07060d]/30 to-transparent" />

        <div className="absolute left-3 top-3">
          <CreatorProofBadge
            label={edit.contentType}
            platform={edit.platform}
            accent={edit.accent}
          />
        </div>

        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full border border-white/20 backdrop-blur-md transition-transform duration-300 group-hover:scale-110"
            style={{ backgroundColor: `${accentColor}33` }}
          >
            <Play size={22} className="ml-0.5 text-white" fill="white" />
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-5">
        <h3 className="text-base font-semibold text-white">{edit.title}</h3>
        <p className="text-xs font-medium" style={{ color: accentColor }}>
          @{edit.creatorName}
        </p>
        <p className="mt-1 text-sm leading-relaxed text-zinc-400">
          {edit.description}
        </p>
      </div>
    </button>
  );
}

function VideoModal({ edit, onClose }) {
  if (!edit) return null;

  const accentColor = edit.accent === "purple" ? PURPLE : MINT;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a12]"
        onClick={(event) => event.stopPropagation()}
        style={{ boxShadow: `0 30px 80px -20px ${accentColor}66` }}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center rounded-full border border-white/10 bg-black/60 text-white/80 backdrop-blur-md transition hover:bg-black/80 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="aspect-video w-full bg-black">
          {edit.src ? (
            <video
              src={edit.src}
              poster={edit.thumbnail}
              controls
              autoPlay
              className="h-full w-full object-contain"
            />
          ) : edit.thumbnail ? (
            <img
              src={edit.thumbnail}
              alt={edit.title}
              className="h-full w-full object-contain"
            />
          ) : (
            <div
              className="grid h-full w-full place-items-center"
              style={{
                background: `linear-gradient(135deg, ${accentColor}33 0%, #0a0a0f 70%)`,
              }}
            >
              <p className="text-sm text-zinc-400">Preview coming soon</p>
            </div>
          )}
        </div>

        <div className="p-6">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <CreatorProofBadge
              label={edit.contentType}
              platform={edit.platform}
              accent={edit.accent}
            />
          </div>
          <h3 className="text-xl font-semibold text-white">{edit.title}</h3>
          <p className="mt-1 text-sm font-medium" style={{ color: accentColor }}>
            @{edit.creatorName}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-400">
            {edit.description}
          </p>
        </div>
      </div>
    </div>
  );
}

const creatorEdits = [
  {
    title: "I Beat the Rank 3 Karthus Player… With Kayn Top",
    creatorName: "Legostomper37",
    contentType: "Twitch Highlight",
    platform: "Twitch",
    src: "https://www.youtube.com/watch?v=wKNJ_zUVe78&t=319s",
    thumbnail: "https://img.youtube.com/vi/wKNJ_zUVe78/maxresdefault.jpg",
    description:
      "13 minute video edited for Masters Kayn top player vs Karhus.",
    accent: "mint",
  },
  {
    title: "PALANTIR PUT ME IN LOSERS QUEUE !!!",
    creatorName: "Lolgunslinger",
    contentType: "Twitch Highlight",
    platform: "Twitch",
    src: "https://www.youtube.com/watch?v=g_JVFGEqj70",
    thumbnail: "https://img.youtube.com/vi/g_JVFGEqj70/maxresdefault.jpg",
    description:
      "A short-form edit designed for retention, quick hooks, and clean visual pacing.",
    accent: "purple",
  }
];

export default function CreatorProofSection() {
  const [active, setActive] = useState(null);

  return (
    <section className="relative px-5 py-20 sm:px-8 sm:py-28">
      <div
        className="pointer-events-none absolute left-1/4 top-10 z-0 h-72 w-72 rounded-full blur-[120px]"
        style={{ backgroundColor: `${MINT}22` }}
      />
      <div
        className="pointer-events-none absolute bottom-10 right-1/4 z-0 h-72 w-72 rounded-full blur-[120px]"
        style={{ backgroundColor: `${PURPLE}22` }}
      />

      <div className="relative z-10 mx-auto max-w-7xl">
        <div className="mb-6 flex justify-center">
          <span
            className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold backdrop-blur-md"
            style={{
              backgroundColor: `${MINT}15`,
              borderColor: `${MINT}40`,
              color: MINT,
            }}
          >
            <Play size={12} fill={MINT} />
            Early creator edits from the Kre8 team
          </span>
        </div>

        <h2 className="mx-auto max-w-3xl text-center text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Real creator edits.{" "}
          <span
            style={{
              background: `linear-gradient(120deg, ${MINT}, #ffffff 50%, ${PURPLE})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            View previous orders here.
          </span>
        </h2>

        <p className="mx-auto mt-5 max-w-2xl text-center text-base leading-relaxed text-zinc-400 sm:text-lg">
          Early examples edited by the Kre8 team, built around the Twitch,
          YouTube, TikTok, and gaming workflows creators actually need.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-2">
          {creatorEdits.map((edit) => (
            <CreatorEditCard key={edit.title} edit={edit} onOpen={setActive} />
          ))}
        </div>

        <p className="mx-auto mt-10 max-w-2xl text-center text-sm leading-relaxed text-zinc-500">
          Before Kre8 becomes a huge marketplace, we want visitors to see the
          standard we care about: clean edits, creator-first communication, and
          content that fits the platform it is made for.
        </p>

        <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
          <Link
            to="/gaming-video-editors"
            className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-[#07060d] transition-transform hover:-translate-y-0.5"
            style={{
              background: `linear-gradient(135deg, ${MINT}, ${PURPLE})`,
              boxShadow: `0 10px 30px -10px ${MINT}88`,
            }}
          >
            Find an Editor
            <ArrowRight size={16} />
          </Link>
          <Link
            to="/signup"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-6 py-3 text-sm font-semibold text-white backdrop-blur-md transition hover:bg-white/[0.08]"
          >
            Become an Editor
          </Link>
        </div>

        <p className="mt-5 text-center text-xs text-zinc-500">
          Built for Twitch streamers who need consistent edits and editors who
          understand gaming content.
        </p>
      </div>

      <VideoModal edit={active} onClose={() => setActive(null)} />
    </section>
  );
}
